import { useEffect, useMemo, useState } from 'react'
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import {
  ASSETS_TYPES,
  IEcommerceAnswerSchema,
  IEcommerceProductGallery,
  IEcommerceQuestionSchema,
  IEcommerceVideoAsset,
  IFileAsset,
  assetsCollectionElement
} from '../../types'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import { assetDialogEvent } from '../../utils/assetDialogEvent'
import { cleanBrandTitle } from '../../utils/cleanBrandTitle'
import { defaultAssetIndex } from '../../utils/defaultAssetIndex'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale'
import { getAssetType } from '../../utils/getAssetType'
import { replaceTags } from '../../utils/replaceTags'
import { getBigAssetPath, getMediumAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import AssetsGate from '../../components/AssetsGate.tsx'
import { qtyCap } from '../functions'
import { unescapeTags } from '@aytm/lib/common/func.js'
import Rating from './Rating'
import AssetsModal from './AssetsModal'
import Spinner from './Spinner'
import Subproducts from './Subproducts'
import ZoomableAsset from './ZoomableAsset'
import style from './Product.module.scss'
import commonStyle from '../Chewy.module.scss'
import autoshipLogo from '../assets/icons/autoship-logo.svg'
import autoshipSavingsIcon from '../assets/icons/autoship-savings.svg'
import autoshipScheduleIcon from '../assets/icons/autoship-schedule.svg'
import autoshipSkipIcon from '../assets/icons/autoship-skip.svg'
import byChewyLogo from '../assets/icons/by-chewy-logo.svg'
import useAssetsCollectionBuilder from '../../hooks/useAssetsCollectionBuilder.ts'

interface IProductProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  findability: boolean
  amount: number
  onAddToCart: (index: number, qnt?: number) => void
  onSetQuantity: (index: number, qnt: number) => void
  onItemSelect: (index: number) => void
  onItemClose: () => void
}

const Product = ({
  schema,
  answer,
  findability,
  amount,
  onAddToCart,
  onSetQuantity,
  onItemSelect,
  onItemClose
}: IProductProps) => {
  const { t, languageTag } = useSurveyLocale()
  const timeline = useTimeline()
  const viewport = useViewport()

  const orderedAssetsCollection = useAssetsCollectionBuilder(answer)

  const [modalAssetIndex, setModalAssetIndex] = useState<number | null>(null)
  const [qty, setQty] = useState<number>(1)
  useEffect(() => setQty(1), [answer.id])
  const defaultIndex = defaultAssetIndex(orderedAssetsCollection)
  const [activeAssetIndex, setActiveAssetIndex] = useState<number>(defaultIndex)
  useEffect(() => setActiveAssetIndex(defaultIndex), [answer.id, defaultIndex])
  const safeActiveIndex = Math.min(activeAssetIndex, orderedAssetsCollection.length - 1)
  // Only the photo column waits, and only for the pictures it first shows: on the phone the opening photo and
  // the thumb rail, on the desktop the top of the column, which fits about two square slots. The photos further
  // down load as they scroll in. The opening photo is the product's default, not the active one, so tapping a
  // thumb never brings the spinner back; another product does.
  const imageOf = (asset: assetsCollectionElement) =>
    asset && ASSETS_TYPES.IMAGE === getAssetType(asset) ? (asset as IImageAsset) : null
  const pageImages = viewport.mobile
    ? [
        imageOf(orderedAssetsCollection[defaultIndex]) &&
          getMediumAssetPath(imageOf(orderedAssetsCollection[defaultIndex])),
        ...(orderedAssetsCollection.length > 1
          ? orderedAssetsCollection.map(asset => imageOf(asset) && getSmallAssetPath(imageOf(asset)))
          : [])
      ]
    : orderedAssetsCollection.slice(0, 2).map(asset => imageOf(asset) && getBigAssetPath(imageOf(asset)))
  const cap = qtyCap(schema.max_qty_per_product, findability)
  const hasCap = !findability && schema.max_qty_per_product > 0
  const atCap = hasCap && amount >= schema.max_qty_per_product
  // Pre-add selector offers only the remaining headroom (cap − units already in cart) so the
  // increment add can't silently clamp; unlimited mode keeps the display cap. Min 1 stays valid.
  const selectableQty = hasCap ? Math.max(schema.max_qty_per_product - amount, 1) : cap
  const quantityOptions = useMemo(() => Array.from({ length: selectableQty }, (_, i) => i + 1), [selectableQty])
  const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
  const buyOncePrice = hasDiscount ? answer.discounted_price : answer.price
  const autoshipPrice = buyOncePrice * 0.95
  const brand = answer.sub_title ? cleanBrandTitle(answer.sub_title) : ''

  const onAssetClick = (assetIndex: number) => {
    timeline.log({
      ref: answer.id,
      type: assetDialogEvent('open', orderedAssetsCollection[assetIndex])
    })
    setModalAssetIndex(assetIndex)
  }

  // The modal logs the typed close itself — it owns the on-screen asset the close is typed by.
  const onAssetsModalClose = () => setModalAssetIndex(null)

  const onClickAddToCart = () => {
    if (findability) return onSetQuantity(answer.index, qty)
    onAddToCart(answer.index, qty)
    setQty(1) // selector returns to 1 so it stays within the headroom that just shrank
  }

  const renderAssetMedia = (asset: assetsCollectionElement, assetIndex: number, zoom = true) => {
    const type = getAssetType(asset)
    if (ASSETS_TYPES.IMAGE === type)
      return zoom ? (
        <ZoomableAsset
          asset={asset as IImageAsset | IEcommerceProductGallery}
          answer={answer}
          assetIndex={assetIndex}
        />
      ) : (
        <div
          className={style.flatImage}
          style={{ backgroundImage: `url(${getMediumAssetPath(asset as IImageAsset)})` }}
        />
      )
    if (ASSETS_TYPES.VIDEO === type)
      return (
        <div
          className={style.video}
          style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
        >
          <span className={style.playIcon} />
        </div>
      )
    if (ASSETS_TYPES.MODEL === type)
      return (
        <div className={style.model}>
          <model-viewer
            src={(asset as IFileAsset).file_url}
            camera-controls={false}
            interaction-prompt="none"
            style={{ width: '100%', height: '100%' }}
          />
          <div className={style.modelOverlay} />
        </div>
      )
    return null
  }

  // Small thumbnail preview for the mobile gallery rail.
  const renderThumb = (asset: assetsCollectionElement) => {
    const type = getAssetType(asset)
    const url =
      ASSETS_TYPES.IMAGE === type
        ? getSmallAssetPath(asset as IImageAsset)
        : ASSETS_TYPES.VIDEO === type
          ? (asset as IEcommerceVideoAsset).video.thumbnail
          : null
    if (!url) return <i className={`pi pi-box ${style.thumbModelIcon}`} />
    return (
      <span className={style.thumbImage} style={{ backgroundImage: `url(${url})` }}>
        {ASSETS_TYPES.VIDEO === type && <span className={style.thumbPlayIcon} />}
      </span>
    )
  }

  const headerInfo = (
    <>
      {/* HTML by design (author markup / search highlight); XSS-safe — sanitized server-side on ingress: author via SurveySchemaTexts.safe_string?, import via Trajectdata::DataSanitizer. */}
      <h1 className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />
      {brand && (
        <div className={style.brand}>
          <span className={style.brandBy}>{t('questions', 'ec', 'chewy', 'by')}</span>
          <EmptyLink color="#1C49C4">
            <span className={style.brandName} dangerouslySetInnerHTML={{ __html: brand }} />
          </EmptyLink>
        </div>
      )}
      <div className={style.ratingRow}>
        <span className={style.ratingValue}>{(answer.rating ?? 0).toFixed(1)}</span>
        <Rating rating={answer.rating ?? 0} />
        {answer.reviews ? (
          <EmptyLink color="#1C49C4">
            <span className={style.reviewsLink}>
              {answer.reviews} {t('questions', 'ec', 'chewy', 'ratings')}
            </span>
          </EmptyLink>
        ) : (
          <EmptyLink color="#1C49C4">
            <span className={style.reviewsLink}>{t('questions', 'ec', 'chewy', 'writeFirstReview')}</span>
          </EmptyLink>
        )}
      </div>

      {answer.amazon_choice && (
        <div className={`${commonStyle.byChewy} ${style.byChewyLine}`}>
          <img src={byChewyLogo} alt="Chewy" className={commonStyle.byChewyLogo} />
        </div>
      )}
    </>
  )

  return (
    <section className={`${style.product} ${viewport.mobile ? style.mobile : ''}`} data-testid="SW-EC-PDP">
      <div className={style.body}>
        {viewport.mobile && <div className={style.mobileHeader}>{headerInfo}</div>}
        <AssetsGate
          urls={pageImages}
          loader={
            <div className={style.col1}>
              <Spinner />
            </div>
          }
        >
          <div className={style.col1}>
            {!viewport.mobile && (
              <button type="button" className={style.backLink} onClick={onItemClose}>
                <i className="pi pi-angle-left" />
                <span>{t('questions', 'ec', 'chewy', 'backToResults')}</span>
              </button>
            )}

            {/* PDP first column shows both badges in sequence: red_label then overal_pick ("New"). */}
            {!viewport.mobile && (answer.red_label || answer.overal_pick) && (
              <div className={style.col1BadgeRow}>
                {answer.red_label && (
                  <div className={commonStyle.redLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />
                )}
                {answer.overal_pick && <div className={commonStyle.newBadge} />}
              </div>
            )}

            {orderedAssetsCollection.length === 0 ? (
              <div className={style.noImage} />
            ) : viewport.mobile ? (
              <div className={style.galleryMobile}>
                <div className={style.galleryActive} onClick={() => onAssetClick(safeActiveIndex)}>
                  {(answer.red_label || answer.overal_pick) && (
                    <div className={commonStyle.badgeRow}>
                      {answer.red_label && (
                        <div className={commonStyle.redLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />
                      )}
                      {answer.overal_pick && <div className={commonStyle.newBadge} />}
                    </div>
                  )}
                  <div className={style.galleryActions} onClick={event => event.stopPropagation()}>
                    <EmptyLink color="#1C49C4">
                      <div className={style.galleryAction} aria-label={t('questions', 'ec', 'chewy', 'addToWishlist')}>
                        <i className="pi pi-heart" />
                      </div>
                    </EmptyLink>
                    <EmptyLink color="#1C49C4">
                      <div className={style.galleryAction} aria-label={t('questions', 'ec', 'chewy', 'share')}>
                        <i className="pi pi-share-alt" />
                      </div>
                    </EmptyLink>
                  </div>
                  {renderAssetMedia(orderedAssetsCollection[safeActiveIndex], safeActiveIndex, false)}
                </div>
                {orderedAssetsCollection.length > 1 && (
                  <div className={style.galleryThumbsWrap}>
                    <div className={style.galleryThumbs}>
                      {orderedAssetsCollection.map((asset, assetIndex) => (
                        <button
                          type="button"
                          key={assetIndex}
                          className={`${style.galleryThumb} ${assetIndex === safeActiveIndex ? style.galleryThumbActive : ''}`}
                          onClick={() => setActiveAssetIndex(assetIndex)}
                        >
                          {renderThumb(asset)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className={style.assets}>
                  {orderedAssetsCollection.map((asset, assetIndex) => (
                    <div key={assetIndex} className={style.assetSlot} onClick={() => onAssetClick(assetIndex)}>
                      {renderAssetMedia(asset, assetIndex)}
                    </div>
                  ))}
                </div>
                <div className={style.assetsBlur} />
              </>
            )}
          </div>
        </AssetsGate>

        <div className={style.col2}>
          {!viewport.mobile && headerInfo}
          <Subproducts schema={schema} answer={answer} onItemSelect={onItemSelect} />
        </div>

        <div className={style.col3} data-testid="SW-EC-PDP-AddToCartSidebar">
          <div className={`${style.priceCard} ${style.priceCardActive}`}>
            <EmptyLink color="#1C49C4">
              <span className={`${style.priceCardRadio} ${style.priceCardRadioActive}`}>
                <span className={style.priceCardRadioDot} />
              </span>
            </EmptyLink>
            <div className={style.priceCardContent}>
              <span className={style.priceCardLabel}>{t('questions', 'ec', 'chewy', 'buyOnce')}</span>
              <div className={style.priceCardPriceRow}>
                <span
                  className={`${style.priceCardPrice} ${hasDiscount ? style.priceCardPriceDiscounted : ''}`}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(
                      hasDiscount ? answer.discounted_price : answer.price,
                      schema.price_mask,
                      languageTag,
                      true,
                      false
                    )
                  }}
                />
                {answer.per_count_price && (
                  <span className={style.priceCardPerCount}>
                    (<span dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }} />)
                  </span>
                )}
              </div>
              {hasDiscount && (
                <div className={style.priceCardSavingsRow}>
                  <span className={style.priceCardSavings}>
                    {t('questions', 'ec', 'chewy', 'save')}{' '}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: formatPriceWithLocale(
                          answer.price - answer.discounted_price,
                          schema.price_mask,
                          languageTag,
                          true,
                          false
                        )
                      }}
                    />{' '}
                    ({Math.round(((answer.price - answer.discounted_price) / answer.price) * 100)}%)
                  </span>
                  <span className={style.priceCardSavingsDot}>&middot;</span>
                  <span
                    className={style.priceCardOldPrice}
                    dangerouslySetInnerHTML={{
                      __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
                    }}
                  />
                  <EmptyLink color="#1C49C4">
                    <i className={`pi pi-info-circle ${style.priceCardInfo}`} />
                  </EmptyLink>
                </div>
              )}
            </div>
          </div>

          <div className={style.priceCard}>
            <EmptyLink color="#1C49C4">
              <span className={style.priceCardRadio} />
            </EmptyLink>
            <div className={style.priceCardContent}>
              <span className={style.autoshipOptionLabel}>
                <img
                  src={autoshipLogo}
                  alt={t('questions', 'ec', 'chewy', 'autoship')}
                  className={style.autoshipOptionLogo}
                />
                <span className={style.autoshipOptionTagline}>{t('questions', 'ec', 'chewy', 'autoshipTagline')}</span>
              </span>
              <div className={style.priceCardPriceRow}>
                <span
                  className={style.priceCardPrice}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(autoshipPrice, schema.price_mask, languageTag, true, false)
                  }}
                />
              </div>
              <div className={style.priceCardSavingsRow}>
                <span className={style.priceCardSavings}>
                  {t<(percent: number) => string>('questions', 'ec', 'chewy', 'savePercent')(5)}
                </span>
                <span className={style.priceCardSavingsDot}>&middot;</span>
                <span
                  className={style.priceCardOldPrice}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(buyOncePrice, schema.price_mask, languageTag, true, false)
                  }}
                />
              </div>
              <div className={style.autoshipFuture}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(autoshipPrice, schema.price_mask, languageTag, true, false)
                  }}
                />{' '}
                {t<(percent: number) => string>('questions', 'ec', 'chewy', 'futureOrders')(5)}
              </div>
              <ul className={style.autoshipBenefits}>
                <li>
                  <img src={autoshipSavingsIcon} alt="" role="presentation" className={style.benefitIcon} />
                  {t<(percent: number) => string>('questions', 'ec', 'chewy', 'keepSaving')(5)}
                </li>
                <li>
                  <img src={autoshipScheduleIcon} alt="" role="presentation" className={style.benefitIcon} />
                  {t('questions', 'ec', 'chewy', 'getDeliveries')}
                </li>
                <li>
                  <img src={autoshipSkipIcon} alt="" role="presentation" className={style.benefitIcon} />
                  {t('questions', 'ec', 'chewy', 'skipChangeCancel')}
                </li>
              </ul>
            </div>
          </div>

          <div className={style.qtyRow}>
            <div className={style.qty}>
              <span className={style.qtyLabel}>{t('questions', 'ec', 'chewy', 'quantity')}</span>
              <select className={style.qtyDropdown} value={qty} onChange={e => setQty(Number(e.target.value))}>
                {quantityOptions.map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className={style.qtyInfo}>
              <span className={style.qtyInfoStock}>{t('questions', 'ec', 'chewy', 'inStock')}</span>
              <span className={style.qtyInfoDelivery}>{t('questions', 'ec', 'chewy', 'freeDelivery13')}</span>
            </div>
          </div>

          <div className={style.returnsBlock}>
            <i className={`pi pi-replay ${style.returnsIcon}`} />
            <span>{t('questions', 'ec', 'chewy', 'freeReturns365')}</span>
            <EmptyLink color="#1C49C4">
              <span className={style.detailsLink}>{t('questions', 'ec', 'chewy', 'details')}</span>
            </EmptyLink>
          </div>

          <button
            type="button"
            className={style.addToCart}
            data-testid="SW-EC-AddToCartButton"
            onClick={onClickAddToCart}
            disabled={atCap}
          >
            {t('questions', 'ec', 'chewy', 'addToCart')}
          </button>
        </div>
      </div>

      {(answer.description || answer.feature_bullets) && (
        <div className={style.about}>
          <h2 className={style.aboutTitle}>{t('questions', 'ec', 'chewy', 'aboutItem')}</h2>
          <div
            className={style.description}
            dangerouslySetInnerHTML={{
              __html: replaceTags(unescapeTags(`${answer.feature_bullets || ''} ${answer.description || ''}`))
            }}
          />
          <EmptyLink color="#1C49C4">
            <span className={style.seeMore}>{t('questions', 'ec', 'chewy', 'seeMore')}</span>
          </EmptyLink>
        </div>
      )}

      {modalAssetIndex !== null && (
        <AssetsModal
          answer={answer}
          orderedAssetsCollection={orderedAssetsCollection}
          index={modalAssetIndex}
          onAssetsModalClose={onAssetsModalClose}
        />
      )}
    </section>
  )
}

export default Product
